<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, provide, watch, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Capacitor } from '@capacitor/core'
import AppNavigation from './components/AppNavigation.vue'
import AppFooter from './components/AppFooter.vue'
import ToastContainer from './components/ToastContainer.vue'
import DemoBanner from './components/DemoBanner.vue'
import DemoOnlinePaymentsNotice from './components/DemoOnlinePaymentsNotice.vue'
import DemoVerifyModal from './components/DemoVerifyModal.vue'
import DemoCredentialsSentModal from './components/DemoCredentialsSentModal.vue'
import ImpersonationBanner from './components/ImpersonationBanner.vue'
import { useAuthStore } from './stores/auth'

const authStore = useAuthStore()
const route = useRoute()
const router = useRouter()

// Pages that render their own navigation (e.g. /nav-2 design proposals)
const hideAppChrome = computed(() => route.meta.hideAppChrome === true)

const handleDemoCredentialsSentClose = () => {
  authStore.showDemoCredentialsSent = false
  router.push('/dashboard')
}

watch(
  () => route.fullPath,
  () => {
    nextTick(() => {
      requestAnimationFrame(() => {
        window.scrollTo(0, 0)
        const appEl = document.getElementById('app')
        if (appEl) appEl.scrollTop = 0
      })
    })
  }
)

const isKeyboardOpen = ref(false)
const keyboardHeight = ref(0)
const showSplash = ref(Capacitor.isNativePlatform())
const splashFading = ref(false)

provide('isKeyboardOpen', isKeyboardOpen)
provide('keyboardHeight', keyboardHeight)

let keyboardShowListener: any = null
let keyboardHideListener: any = null

const hideSplash = async () => {
  // Hide native splash screen
  if (Capacitor.isNativePlatform()) {
    const { SplashScreen } = await import('@capacitor/splash-screen')
    await SplashScreen.hide()
  }

  // Start fade-out animation after a short delay for logo to be visible
  setTimeout(() => {
    splashFading.value = true
    // Remove splash overlay after fade-out completes
    setTimeout(() => {
      showSplash.value = false
    }, 600)
  }, 1200)
}

onMounted(async () => {
  if (Capacitor.isNativePlatform()) {
    const { Keyboard } = await import('@capacitor/keyboard')
    keyboardShowListener = await Keyboard.addListener('keyboardWillShow', (info) => {
      isKeyboardOpen.value = true
      keyboardHeight.value = info.keyboardHeight
    })
    keyboardHideListener = await Keyboard.addListener('keyboardWillHide', () => {
      isKeyboardOpen.value = false
      keyboardHeight.value = 0
    })

    // Hide native splash and show animated one
    hideSplash()
  }
})

onUnmounted(() => {
  keyboardShowListener?.remove()
  keyboardHideListener?.remove()
})
</script>

<template>
  <!-- Animated splash overlay (native only) -->
  <div
    v-if="showSplash"
    class="fixed inset-0 z-[9999] flex items-center justify-center bg-white transition-opacity duration-500"
    :class="splashFading ? 'opacity-0' : 'opacity-100'"
  >
    <img
      src="/sportovo_logo_light.svg"
      alt="sportovo.io"
      class="w-64 splash-logo"
    />
  </div>

  <div id="app" :class="{ 'pb-20 md:pb-0': authStore.isAuthenticated && !hideAppChrome }">
    <ImpersonationBanner />
    <template v-if="!hideAppChrome">
      <AppNavigation />
      <DemoBanner />
      <DemoOnlinePaymentsNotice />
    </template>
    <router-view />
    <AppFooter v-if="!hideAppChrome" class="hidden lg:block" />
    <ToastContainer />
    <DemoVerifyModal v-if="authStore.demoStatus === 'pending'" />
    <DemoCredentialsSentModal
      v-if="authStore.showDemoCredentialsSent"
      @close="handleDemoCredentialsSentClose"
    />
  </div>
</template>

<style scoped>
.splash-logo {
  animation: splashFadeScale 0.8s ease-out forwards;
}

@keyframes splashFadeScale {
  0% {
    opacity: 0;
    transform: scale(0.7);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}
</style>
